import * as React from "react";
import { styled } from "@mui/material/styles";
import Editor from "../editor/Editor";
import { observer } from "mobx-react";
import autobind from "autobind-decorator";
import NodeLayer, {
  questionTypes,
  narrativeSplitPoint,
} from "../editor/NodeLayer";
import { observable } from "mobx";
import { Avatar, Box, Button } from "@mui/material";
import { Icon as IconifyIcon } from "@iconify/react";
import robotIcon from "@iconify/icons-mdi/robot";
import ForwardIcon from "@mui/icons-material/Forward";
import ConnectionLayer from "../editor/ConnectionLayer";
import CustomLinkify from "../pages/questionnaire/components/CustomLinkify";
import Icon from "@mdi/react";
import { detectURLs, parseStringStyles } from "../util/util";
import { mdiLinkVariant } from "@mdi/js";



const StyledCustomButton = styled(Button)`
  background-color: ${(props: any) => props.customStyles.backgroundColor || "#3f50b5"};
  color: ${(props: any) => props.customStyles.color || "#fff"};

  &:hover {
    background-color: ${(props: any) =>
    props.customStyles.hoverBackgroundColor || "#002884"};
    color: ${(props: any) => props.customStyles.hoverColor || "#fff"};
  }
`;

const StyledRadioGroup = styled("div")({
  margin: "12px",
  fontSize: "14",
  color: "rgb(120, 120, 120)",
});

const CheckboxContainer = styled("div")({
  margin: "12px",
  fontSize: "14",
  color: "rgb(120, 120, 120)",
});

export class QuestionnaireChatState {
  @observable
  currentQuestionNode: NodeLayer;

  @observable
  questionsArray: NodeLayer[] = [];

  @observable
  currentQuestionPointer: number = 0;

  get isComplete(): boolean {
    return this.currentQuestionNode == null;
  }

  constructor(editor: Editor) {
    this.currentQuestionNode = editor.startLayer;
  }

  handlePrevious() {
    if (this.currentQuestionNode == null) {
      return;
    }
    if (this.currentQuestionPointer > 0 && this.questionsArray.length >= 1) {
      this.currentQuestionNode =
        this.questionsArray[--this.currentQuestionPointer];
    }
  }

  isConditionValid(conn: ConnectionLayer): boolean {
    if (conn.condition == "") {
      return true;
    }
    var conditionList = conn.condition.split(narrativeSplitPoint);

    var intersect = this.currentQuestionNode.answers.filter((it) =>
      conditionList.includes(it)
    );

    return intersect.length != 0;
  }

  handleNext() {
    if (this.currentQuestionNode == null) {
      return;
    }

    for (var conn of this.currentQuestionNode.outgoingConnections) {
      if (this.isConditionValid(conn)) {
        this.questionsArray[this.currentQuestionPointer] =
          this.currentQuestionNode;
        this.currentQuestionNode = conn.endNode;
        this.currentQuestionPointer++;
        return;
      }
    }

    this.currentQuestionNode = null;
  }
}

export type QuestionnaireChatRendererProps = {
  editor: Editor;
  className?: string;
  state: QuestionnaireChatState;
  optioOnClick: any;
  profilePic?: string;
};

@observer
export default class QuestionnaireChatRenderer extends React.Component<QuestionnaireChatRendererProps> {
  @observable
  multiChoiceAns: Array<string>;

  singleOptionOnClick(choice: string) {
    this.props.state.currentQuestionNode.setAnswers([choice]);
    this.props.optioOnClick();
  }

  getCurrentQuestionText(): string {
    var { state } = this.props;

    if (state.currentQuestionNode == null) {
      return "";
    }

    return state.currentQuestionNode.questionText;
  }

  @autobind
  submitMultiChoice() {
    if (this.props.state.currentQuestionNode.answers.length > 0) {
      this.props.optioOnClick();
    }
  }

  renderAnswerSelector() {
    var { state } = this.props;

    if (state.currentQuestionNode == null) {
      return null;
    }

    const yesChoiceStyle = state.currentQuestionNode.yesChoiceStyle;
    const noChoiceStyle = state.currentQuestionNode.noChoiceStyle;
    const choicesStyles = state.currentQuestionNode.choicesStyles

    if (state.currentQuestionNode.questionType == questionTypes.yesNo) {
      return (
        <StyledRadioGroup key="answer-yesno">
          <div style={{ marginBottom: "12px" }}>
            <StyledCustomButton
              variant="contained"
              color={
                state.currentQuestionNode.answers[0] === "Yes"
                  ? "secondary"
                  : "primary"
              }
              onClick={() => this.singleOptionOnClick("Yes")}
              size="small"
              style={{ fontSize: "0.6rem" }}
              customStyles={parseStringStyles(yesChoiceStyle ?? '')}
            >
              Yes
            </StyledCustomButton>
          </div>
          <div>
            <StyledCustomButton
              variant="contained"
              color={
                state.currentQuestionNode.answers[0] === "No"
                  ? "secondary"
                  : "primary"
              }
              onClick={() => this.singleOptionOnClick("No")}
              size="small"
              style={{ fontSize: "0.6rem" }}
              customStyles={parseStringStyles(noChoiceStyle ?? '')}
            >
              No
            </StyledCustomButton>
          </div>
        </StyledRadioGroup>
      );
    }

    if (
      state.currentQuestionNode.questionType == questionTypes.singleChoice ||
      state.currentQuestionNode.questionType == "Single Choice"
    ) {
      return (
        <StyledRadioGroup key="answer-singlechoice">
          {state.currentQuestionNode.choices.map((choice, index) => (
            <Box
              display="flex"
              alignItems="center"
              marginBottom="12px"
              key={`check-${index}`}
            >
              <StyledCustomButton
                variant="contained"
                color={
                  state.currentQuestionNode.answer === choice
                    ? "secondary"
                    : "primary"
                }
                onClick={() => this.singleOptionOnClick(choice)}
                size="small"
                style={{ fontSize: "0.6rem" }}
                customStyles={parseStringStyles(choicesStyles[index] ?? '')}              >
                {choice}
              </StyledCustomButton>
              {detectURLs(choice)?.map((url: any) => {
                const customUrl =
                  url.includes("https") || url.includes("http")
                    ? url
                    : `https://${url}`;
                return (
                  <a href={customUrl} target="_blank" rel="noopener noreferrer">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="25px"
                      height="25px"
                      borderRadius="50%"
                      bgcolor="#3f50b5"
                      marginLeft="6px"
                    >
                      <Icon path={mdiLinkVariant} color="#fff" size={0.6} />
                    </Box>
                  </a>
                );
              })}
            </Box>
          ))}
        </StyledRadioGroup>
      );
    }

    if (
      state.currentQuestionNode.questionType == questionTypes.multiChoice ||
      state.currentQuestionNode.questionType == "Multiple Choice"
    ) {
      let multiChoiceAns = [];
      return (
        <CheckboxContainer>
          {state.currentQuestionNode.choices.map((choice, index) => {
            return (
              <Box
                display="flex"
                alignItems="center"
                marginBottom="12px"
                key={`check-${index}`}
              >
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    backgroundColor:
                      state.currentQuestionNode.answers.indexOf(choice) >= 0
                        ? "#228B22"
                        : "#3f50b5",
                    fontSize: "0.6rem",
                  }}
                  onClick={() => {
                    multiChoiceAns = state.currentQuestionNode.answers;
                    const exists = multiChoiceAns.includes(choice);
                    if (!exists) {
                      multiChoiceAns.push(choice);
                    } else {
                      multiChoiceAns = multiChoiceAns.filter(
                        (i) => i !== choice
                      );
                    }
                    state.currentQuestionNode.setAnswers(multiChoiceAns);
                  }}
                  size="small"
                >
                  {choice}
                </Button>
                {detectURLs(choice)?.map((url: any) => {
                  const customUrl =
                    url.includes("https") || url.includes("http")
                      ? url
                      : `https://${url}`;
                  return (
                    <a
                      href={customUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width="25px"
                        height="25px"
                        borderRadius="50%"
                        bgcolor="#3f50b5"
                        marginLeft="6px"
                      >
                        <Icon path={mdiLinkVariant} color="#fff" size={0.6} />
                      </Box>
                    </a>
                  );
                })}
              </Box>
            );
          })}
          <Button
            variant="contained"
            color="inherit"
            onClick={this.submitMultiChoice}
            size="small"
            style={{ fontSize: "0.6rem" }}
          >
            SUBMIT
            <ForwardIcon style={{ marginLeft: "5px", fontSize: "1rem" }} />
          </Button>
        </CheckboxContainer>
      );
    }
  }

  render() {
    return (
      <Box width="100%" padding="10px 20px" display="flex" flexDirection="row">
        {this.props.profilePic ? (
          <Avatar alt="A" src={this.props.profilePic}></Avatar>
        ) : (
          <Avatar style={{ backgroundColor: "#303F9F" }}>
            <IconifyIcon icon={robotIcon} />
          </Avatar>
        )}

        <Box>
          <Box
            className="containerWithSimpleLink"
            bgcolor="#E9EEF3"
            borderRadius="40px"
            color="#5B5D65"
            textAlign="left"
            alignContent="center"
            padding="10px 20px"
            margin="0px 6px"
            fontSize="12px"
          >
            <CustomLinkify str={this.getCurrentQuestionText()} />
          </Box>
          {this.renderAnswerSelector()}
        </Box>
      </Box>
    );
  }
}
